import React, { Fragment, useEffect, useState } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import FullstoryIcon from "@common/assets/images/integrations/fullstory-icon.svg";
import { ActivityHistoryButton } from "@common/components/DetailsPane/ActivityHistoryButton";
import { DetailsPane } from "@common/components/DetailsPane/DetailsPane";
import { DetailsPaneNudge } from "@common/components/DetailsPane/DetailsPaneNudge";
import {
  getAppUserInfoElements,
  getExtensionsDropdown,
  getTicketDetailsElements,
  GroupedInfoElement,
  GroupedTicketDetailsElement,
  isElementDisabled,
  readMetadata,
} from "@common/components/DetailsPane/helper";
import Heading from "@common/components/Heading/Heading";
import EditableTitle from "@common/components/Input/EditableTitle/EditableTitle";
import { ActivityHistoryNudge, IntegrationsNudge, SentryNudge } from "@common/components/Nudge/Nudge";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { PlatformName } from "@common/models";
import { docLink } from "@common/util/docs";
import identifiers from "@common/util/identifiers.json";
import { SessionReplayContainer } from "@main/pages/shared/components/SessionReplayContainer/SessionReplayContainer";
import TicketMobileViewHeader from "@main/pages/shared/components/TicketMobileViewHeader/TicketMobileViewHeader";
import { useAppSelectionContext } from "@public/context/App/AppSelectionContext";
import { DetailsBar } from "@public/layouts/components/DetailsBar/DetailsBar";
import NavigationCustomElement from "@public/layouts/components/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@public/layouts/components/PageNavigation";
import AttachmentsContainer from "@public/pages/shared/components/Attachments/AttachmentsContainer";
import CustomFields from "@public/pages/shared/components/CustomFields/CustomFields";
import MessageContainer from "@public/pages/shared/components/Messages/MessageContainer";
import { TicketDetailsContainer } from "@public/pages/shared/components/TicketDetails/TicketDetailsContainer";
import { TicketContainer } from "@public/pages/shared/components/TicketDetails/TicketDetailsContainer.styles";
import { TicketInfoContainer } from "@public/pages/shared/components/TicketDetails/TicketInfoContainer";
import * as Styled from "@public/pages/shared/styles/DetailsWrapper.styles";
import useUserFeedbackDetailsApiConsumer from "@public/pages/UserFeedback/consumers/useUserFeedbackDetailsApiConsumer";
import { RoutePaths } from "@public/router/config/routePaths";
import { generatePath, useParams } from "react-router-dom";

export const UserFeedbackDetailsPageView = () => {
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Public user feedback details" });
  }, [sendEvent]);

  const params = useParams<{ issueId: string }>();
  const consumer = useUserFeedbackDetailsApiConsumer({ issueId: params.issueId });
  const [detailDropdownOpened, setDetailDropdownOpened] = useState<string | undefined>(undefined);
  const { selectedApp } = useAppSelectionContext();

  const CustomElement = consumer.userFeedbackDetails ? (
    <NavigationCustomElement>
      <Styled.DetailsTitle data-testid={identifiers["navbar.detailsTitle"]}>
        <p>{consumer.userFeedbackDetails?.pretty_title}</p>
      </Styled.DetailsTitle>
    </NavigationCustomElement>
  ) : (
    <Fragment />
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (consumer.error) {
    return (
      <ErrorStateView
        title="There was an error loading this ticket. Try refreshing."
        subtitle="The link you followed may be broken, or the ticket may have been removed."
      />
    );
  }

  if (!consumer.userFeedbackDetails) {
    return <Spinner />;
  }

  const getSessionLinksElements = () => {
    return [
      {
        infoElements: [
          {
            tip: "Fullstory session",
            icon: FullstoryIcon,
            fullwidth: false,
            Dropdown: getExtensionsDropdown(
              consumer?.userFeedbackDetails?.pretty_fullstory_session ? "Fullstory" : "Not Available",
              "Fullstory",
              FullstoryIcon,
              {
                text: "Fullstory session",
                link: consumer?.userFeedbackDetails?.pretty_fullstory_session ?? "",
              },
              isElementDisabled(consumer?.userFeedbackDetails?.pretty_fullstory_session ?? ""),
              detailDropdownOpened,
              setDetailDropdownOpened,
            ),
          },
        ],
      },
    ];
  };

  const getMetadataElements = (metadata: { [key: string]: string } | undefined): Array<GroupedTicketDetailsElement> => {
    if (!metadata || Object.keys(metadata).length == 0) return [];

    return [
      {
        infoElements: readMetadata(metadata, [], detailDropdownOpened, setDetailDropdownOpened),
      },
    ];
  };

  const infoPane: GroupedInfoElement[] = [];
  infoPane.push({
    title: "Ticket details",
    headingElement: consumer?.userFeedbackDetails?.log ? (
      <ActivityHistoryButton
        to={generatePath(RoutePaths.USER_FEEDBACK_LOGS, {
          issueId: params.issueId ?? null,
        })}
      />
    ) : (
      <ActivityHistoryNudge />
    ),
    infoElements: getTicketDetailsElements(
      consumer.userFeedbackDetails,
      detailDropdownOpened,
      setDetailDropdownOpened,
      selectedApp,
      undefined,
      consumer.blackbox,
      undefined,
    ),
  });
  if (selectedApp?.platform?.name === PlatformName.WEB)
    infoPane.push({
      title: "Session links",
      infoElements: getSessionLinksElements(),
    });
  infoPane.push({
    title: "Ticket integrations",
    infoElements: [],
    emptyView: (
      <DetailsPaneNudge>
        <IntegrationsNudge disabled={true} />
      </DetailsPaneNudge>
    ),
  });
  infoPane.push({
    title: "Ticket metadata",
    infoElements: getMetadataElements(consumer?.userFeedbackDetails?.metadata_),
    link: {
      tooltipText: "Set ticket metadata",
      link: docLink(selectedApp?.platform.name).addTicketMetadata,
      isExternal: true,
      testId: identifiers["details.pane.metadata.link.add"],
    },
  });
  infoPane.push({
    title: "User details",
    infoElements: getAppUserInfoElements(consumer.userFeedbackDetails?.app_user, undefined, undefined, undefined, true),
    link: {
      tooltipText: "Register app user",
      link: docLink(selectedApp?.platform.name).registerAppUser,
      isExternal: true,
    },
  });

  return (
    <PageWrap>
      <TicketContainer>
        <TicketInfoContainer>
          <TicketMobileViewHeader app={selectedApp} />
          <Heading
            as="h2"
            heading2
            marginBottom={0}
          >
            <EditableTitle
              value={consumer.userFeedbackDetails?.pretty_title}
              defaultValue="No description"
              disabled={true}
              onEditConfirm={(title?: string) => title}
              testId={identifiers["central.column.user.feedback.title"]}
            />
          </Heading>

          <DetailsBar details={consumer.userFeedbackDetails} />
          <CustomFields customFields={consumer.userFeedbackDetails.custom_fields} />

          {consumer.userFeedbackDetails.session_replay_url && (
            <SessionReplayContainer sessionReplayUrl={consumer.userFeedbackDetails.session_replay_url} />
          )}

          <SentryNudge disabled={true} />

          <AttachmentsContainer
            deviceOrientation={consumer.userFeedbackDetails.pretty_device_orientation}
            attachments={consumer.mappedAttachments.sort((a, b) => a.num - b.num)}
          />

          <MessageContainer
            messages={consumer.messages}
            appUser={consumer.userFeedbackDetails.app_user}
          />
        </TicketInfoContainer>
        <TicketDetailsContainer>
          <DetailsPane
            info={infoPane}
            headerTestId={identifiers["user.feedback.details.pane.header"]}
          />
        </TicketDetailsContainer>
      </TicketContainer>
    </PageWrap>
  );
};
